import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import type { StatusBucketMetrics } from "../../utils/dashboardMetrics";
import { formatDashboardMoney } from "../../utils/dashboardMetrics";

interface MarketOverviewProps {
  categories: string[];
  revenueSeries: number[];
  bookingsSeries: number[];
  orderStatus: {
    confirmed: StatusBucketMetrics;
    pending: StatusBucketMetrics;
    cancelled: StatusBucketMetrics;
  };
  currency?: string;
  loading?: boolean;
}

const MarketOverview = ({
  categories,
  revenueSeries,
  bookingsSeries,
  orderStatus,
  currency,
  loading,
}: MarketOverviewProps) => {
  const barchartColors = ["#5156be", "#34c38f"];
  const hasChartData =
    revenueSeries.some((v) => v > 0) || bookingsSeries.some((v) => v > 0);

  const options: Object = {
    chart: {
      type: "bar",
      height: 400,
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "20%",
      },
    },
    colors: barchartColors,
    fill: {
      opacity: 1,
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: "top",
    },
    yaxis: [
      {
        labels: {
          formatter: function (y: any) {
            return Number(y || 0).toFixed(0);
          },
        },
        title: { text: "Revenue" },
      },
      {
        opposite: true,
        labels: {
          formatter: function (y: any) {
            return Number(y || 0).toFixed(0);
          },
        },
        title: { text: "Bookings" },
      },
    ],
    xaxis: {
      categories: categories.length ? categories : ["—"],
      labels: {
        rotate: -45,
      },
    },
  };

  const series = [
    { name: "Revenue", type: "bar", data: hasChartData ? revenueSeries : [0] },
    {
      name: "Bookings",
      type: "bar",
      data: hasChartData ? bookingsSeries : [0],
    },
  ];

  const sideItems = [
    {
      label: "Confirmed",
      value: `+${orderStatus.confirmed.count}`,
      tone: "success",
      detail: formatDashboardMoney(orderStatus.confirmed.amount, currency),
    },
    {
      label: "Pending",
      value: `${orderStatus.pending.count}`,
      tone: "warning",
      detail: formatDashboardMoney(orderStatus.pending.amount, currency),
    },
    {
      label: "Cancelled",
      value: `${orderStatus.cancelled.count}`,
      tone: "danger",
      detail: formatDashboardMoney(orderStatus.cancelled.amount, currency),
    },
  ];

  return (
    <React.Fragment>
      <Col xl={8}>
        <Card>
          <CardBody>
            <div className="d-flex flex-wrap align-items-center mb-4">
              <h5 className="card-title me-2">Revenue & Bookings Overview</h5>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Row className="align-items-center">
                <Col xl={8}>
                  <div>
                    <div id="market-overview" className="apex-charts">
                      {!hasChartData ? (
                        <div className="text-center text-muted py-5">
                          No data available for this period
                        </div>
                      ) : (
                        <ReactApexChart
                          options={options}
                          series={series}
                          type="bar"
                          className="apex-charts"
                          height={400}
                        />
                      )}
                    </div>
                  </div>
                </Col>
                <Col xl={4}>
                  <div className="p-4">
                    {sideItems.map((item, index) => (
                      <div key={item.label} className={index === 0 ? "mt-0" : "mt-3"}>
                        <div className="d-flex align-items-center">
                          <div className="avatar-sm m-auto">
                            <span className="avatar-title rounded-circle bg-light-subtle text-dark font-size-16">
                              {index + 1}
                            </span>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <span className="font-size-16">{item.label}</span>
                            <p className="text-muted mb-0 font-size-12">{item.detail}</p>
                          </div>
                          <div className="flex-shrink-0">
                            <span
                              className={`badge rounded-pill bg-${item.tone}-subtle text-${item.tone} font-size-12 fw-medium`}
                            >
                              {item.value}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="mt-4 pt-2">
                      <Link to="/orders-list" className="btn btn-primary w-100">
                        See All Orders <i className="mdi mdi-arrow-right ms-1"></i>
                      </Link>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default MarketOverview;
